import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import Select, { components } from 'react-select';
import { toast } from 'react-toastify';
import CityStateZipComponent from './CityStateZipComponent';

import 'react-toastify/dist/ReactToastify.css';
import 'react-datepicker/dist/react-datepicker.css';
import './patientForm.css';

// We can keep these options outside the component
const clinicalStageOptions = [
  { value: 'T0', label: 'T0' },
  { value: 'T1a', label: 'T1a' },
  { value: 'T1b', label: 'T1b' },
  { value: 'T1c', label: 'T1c' },
  { value: 'T2a', label: 'T2a' },
  { value: 'T2b', label: 'T2b' },
  { value: 'T2c', label: 'T2c' },
  { value: 'T3a', label: 'T3a' },
  { value: 'T3b', label: 'T3b' },
  { value: 'T4', label: 'T4' }
];

const PatientForm = ({ setPatientData }) => {
  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    control,
    watch,
    setValue
  } = useForm({
    mode: 'onChange',  // enable live validation
  });

  // Watch fields for dynamic behavior
  const dobValue = watch('dob');
  const collectionDateValue = watch('collectionDate');
  // const clinicalStageValue = watch('clinicalStage');

  const [clinicalStageFocused, setClinicalStageFocused] = useState(false);


  const calculateAgeAtBiopsy = (dob, collectionDate) => {
    if (dob && collectionDate) {
      const dobDate = new Date(dob);
      const collection = new Date(collectionDate);
      let age = collection.getFullYear() - dobDate.getFullYear();
      const monthDiff = collection.getMonth() - dobDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && collection.getDate() < dobDate.getDate())) {
        age--;
      }
      return age;
    }
    return '';
  };
  const ageAtBiopsy = calculateAgeAtBiopsy(dobValue, collectionDateValue);

  // A small custom component so we only show the arrow if the select is open
  const DropdownIndicator = (props) => {
    const { selectProps } = props;
    if (selectProps.menuIsOpen) {
      return <components.DropdownIndicator {...props} />;
    }
    return null;
  };

  // Final submission
  const onSubmit = (formData) => {
    // format date fields to mm/dd/yyyy
    const formatDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US');
    };

    const finalData = {
      ...formData,
      dob: formatDate(formData.dob),
      dateOfDiagnosis: formatDate(formData.dateOfDiagnosis),
      collectionDate: formatDate(formData.collectionDate),
      receivedDate: formatDate(formData.receivedDate),
      reportDate: formatDate(formData.reportDate),
      ageAtBiopsy: ageAtBiopsy // If you'd like to store this
    };

    // Pass up to parent; can navigate next, etc.
    setPatientData(finalData);
    toast.success('Form submitted successfully!');
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* PATIENT INFORMATION */}
        <div className="form-section">
          <h2>Patient Information</h2>
          <div className="input-container">
            {/* Patient Name */}
            <div>
              <input
                type="text"
                id="patientName"
                placeholder=" "
                {...register('patientName', { required: 'Patient Name is required' })}
              />
              <label htmlFor="patientName">Patient Name</label>
              {errors.patientName && (
                <p style={{ color: 'red' }}>{errors.patientName.message}</p>
              )}
            </div>

            <Controller
              name="dob"                             // the name in form data
              control={control}                      // pass in the form's control object
              rules={{ required: 'DOB is required' }}// validation rules
              render={({ field, fieldState: { error } }) => {
                // `field.value` is the current value, `field.onChange` updates it
                // `error` is any validation error for this field
                const hasValueClass = field.value ? 'has-value' : '';
                return (
                  <div className="datepicker-container">
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText=" "
                      className={`date-input ${hasValueClass}`}
                      showYearDropdown
                      showMonthDropdown
                      yearDropdownItemNumber={150}
                      scrollableYearDropdown
                      portalId="root-portal"
                    />
                    <label className={field.value ? 'active' : ''}>
                      Date of Birth
                    </label>
                    {error && (
                      <p style={{ color: 'red' }}>{error.message}</p>
                    )}
                  </div>
                );
              }}
            />

            {/* Medical Record */}
            <div>
              <input
                type="text"
                id="medicalRecord"
                placeholder=" "
                {...register('medicalRecord', { required: 'Medical Record is required' })}
              />
              <label htmlFor="medicalRecord">Medical Record</label>
              {errors.medicalRecord && (
                <p style={{ color: 'red' }}>{errors.medicalRecord.message}</p>
              )}
            </div>

            {/* preBiopsyPsa with numeric validation */}
            <div>
              <input
                type="text"
                id="preBiopsyPsa"
                placeholder=" "
                {...register('preBiopsyPsa', {
                  required: 'PSA is required',
                  pattern: {
                    value: /^\d+([.,]\d+)?$/,
                    message: 'PSA must be a valid number'
                  }
                })}
              />
              <label htmlFor="preBiopsyPsa">Pre-Biopsy PSA</label>
              {errors.preBiopsyPsa && (
                <p style={{ color: 'red' }}>{errors.preBiopsyPsa.message}</p>
              )}
            </div>

            {/* Biopsy Result */}
            <div>
              <input
                type="text"
                id="biopsyResult"
                placeholder=" "
                {...register('biopsyResult', {
                  required: 'Biopsy Result is required'
                })}
              />
              <label htmlFor="biopsyResult">Biopsy Result</label>
              {errors.biopsyResult && (
                <p style={{ color: 'red' }}>{errors.biopsyResult.message}</p>
              )}
            </div>

            <Controller
              name="clinicalStage"
              control={control}
              rules={{ required: 'Clinical Stage is required' }}
              // render={({ field, fieldState: { error } }) => {
              //   // If the user has already chosen a value, get the matching option:
              //   const selectedOption = clinicalStageOptions.find(
              //     (opt) => opt.value === field.value
              //   );

              render={({ field, fieldState: { error } }) => {
                // "hasValue" checks if we already have a selected stage
                const hasValue = !!field.value;
        
                // Look up current option
                const selectedOption = clinicalStageOptions.find(
                  (opt) => opt.value === field.value
                );

                return (
                  <div className="clinical-stage-container dropdown-wrapper" id="clinical-stage">
                    <Select
                      placeholder=""
                      options={clinicalStageOptions}
                      value={selectedOption || null}
                      onChange={(option) => field.onChange(option?.value)}
                      onFocus={() => setClinicalStageFocused(true)}
                      onBlur={(e) => {
                        field.onBlur(e);
                        setClinicalStageFocused(false);
                      }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPlacement="auto"
                      components={{ DropdownIndicator }}
                      isSearchable={false}
                    />
                    {/* Label is "active" if there's a value OR the user is focused */}
                    <label className={hasValue || clinicalStageFocused ? 'active' : ''}>
                      Clinical Stage
                    </label>
                    {error && (
                      <p style={{ color: 'red' }}>{error.message}</p>
                    )}
                  </div>
                );
              }}
            />

          </div>
        </div>

        {/* SPECIMEN INFORMATION */}
        <div className="form-section">
          <h2>Specimen Information</h2>
          <div className="input-container">
            {/* dateOfDiagnosis */}
            <Controller
              name="dateOfDiagnosis"
              control={control}
              rules={{ required: 'Date of Diagnosis is required' }} 
              render={({ field, fieldState: { error } }) => {
                return (
                  <div className="datepicker-container">
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText=" "
                      className={`date-input ${field.value ? 'has-value' : ''}`}
                      showYearDropdown
                      showMonthDropdown
                      yearDropdownItemNumber={150}
                      scrollableYearDropdown
                      portalId="root-portal"
                    />
                    <label className={field.value ? 'active' : ''}>
                      Date of Diagnosis
                    </label>
                    {error && <p style={{ color: 'red' }}>{error.message}</p>}
                  </div>
                );
              }}
            />

            {/* specimen */}
            <div>
              <input
                type="text"
                id="specimen"
                placeholder=" "
                {...register('specimen', { required: 'Specimen is required' })}
              />
              <label htmlFor="specimen">Pathology Specimen</label>
              {errors.specimen && (
                <p style={{ color: 'red' }}>{errors.specimen.message}</p>
              )}
            </div>

            <Controller
              name="collectionDate"
              control={control}
              rules={{ required: 'Collection Date is required' }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <div className="datepicker-container">
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText=" "
                      className={`date-input ${field.value ? 'has-value' : ''}`}
                      showYearDropdown
                      showMonthDropdown
                      yearDropdownItemNumber={150}
                      scrollableYearDropdown
                      portalId="root-portal"
                    />
                    <label className={field.value ? 'active' : ''}>
                      Collection Date
                    </label>

                    {/* Show an error if required */}
                    {error && (
                      <p style={{ color: 'red' }}>{error.message}</p>
                    )}
                  </div>
                );
              }}
            />

            <Controller
              name="receivedDate"
              control={control}
              rules={{ required: 'Received Date is required' }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <div className="datepicker-container">
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText=" "
                      className={`date-input ${field.value ? 'has-value' : ''}`}
                      showYearDropdown
                      showMonthDropdown
                      yearDropdownItemNumber={150}
                      scrollableYearDropdown
                      portalId="root-portal"
                    />
                    <label className={field.value ? 'active' : ''}>
                      Received Date
                    </label>
                    {error && (
                      <p style={{ color: 'red' }}>{error.message}</p>
                    )}
                  </div>
                );
              }}
            />

            <Controller
              name="reportDate"
              control={control}
              rules={{ required: 'Report Date is required' }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <div className="datepicker-container">
                    <DatePicker
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText=" "
                      className={`date-input ${field.value ? 'has-value' : ''}`}
                      showYearDropdown
                      showMonthDropdown
                      yearDropdownItemNumber={150}
                      scrollableYearDropdown
                      portalId="root-portal"
                    />
                    <label className={field.value ? 'active' : ''}>
                      Report Date
                    </label>
                    {error && <p style={{ color: 'red' }}>{error.message}</p>}
                  </div>
                );
              }}
            />

            {/* accession */}
            <div>
              <input
                type="text"
                id="accession"
                placeholder=" "
                {...register('accession', {
                  required: 'Accession Number is required'
                })}
              />
              <label htmlFor="accession">PGi Accession Number</label>
              {errors.accession && (
                <p style={{ color: 'red' }}>{errors.accession.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* PROVIDER INFORMATION */}
        <div className="form-section">
          <h2>Provider Information</h2>
          <div className="input-container">
            {/* accountName */}
            <div>
              <input
                type="text"
                id="accountName"
                placeholder=" "
                {...register('accountName', {
                  required: 'Account Name is required'
                })}
              />
              <label htmlFor="accountName">Account Name</label>
              {errors.accountName && (
                <p style={{ color: 'red' }}>{errors.accountName.message}</p>
              )}
            </div>

            {/* accountNumber */}
            <div>
              <input
                type="text"
                id="accountNumber"
                placeholder=" "
                {...register('accountNumber', {
                  required: 'Account Number is required'
                })}
              />
              <label htmlFor="accountNumber">Account Number</label>
              {errors.accountNumber && (
                <p style={{ color: 'red' }}>{errors.accountNumber.message}</p>
              )}
            </div>

             {/* City/State/Zip using subcomponent */}
            <CityStateZipComponent 
              control={control} 
              errors={errors}
              setValue={setValue}
              isSubmitted={isSubmitted}
            />
            
            {/* zipCode (if not handled in CityStateZipComponent) */}
            <div>
              <input
                type="text"
                id="zipCode"
                placeholder=" "
                {...register('zipCode', {
                  required: 'Zip Code is required'
                })}
              />
              <label htmlFor="zipCode">Zip Code</label>
              {errors.zipCode && (
                <p style={{ color: 'red' }}>{errors.zipCode.message}</p>
              )}
            </div>

            {/* address */}
            <div>
              <input
                type="text"
                id="address"
                placeholder=" "
                {...register('address', { required: 'Address is required' })}
              />
              <label htmlFor="address">Address</label>
              {errors.address && (
                <p style={{ color: 'red' }}>{errors.address.message}</p>
              )}
            </div>

            {/* orderingPhysician */}
            <div>
              <input
                type="text"
                id="orderingPhysician"
                placeholder=" "
                {...register('orderingPhysician', {
                  required: 'Ordering Physician is required'
                })}
              />
              <label htmlFor="orderingPhysician">Ordering Physician</label>
              {errors.orderingPhysician && (
                <p style={{ color: 'red' }}>{errors.orderingPhysician.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* SUBMIT */}
        <div className="patient-form-submittion">
          <button 
            type="submit" 
            className={`btn-submit-patient-form ${isValid ? 'valid' : 'invalid'}`}
          >
            Next
          </button>
        </div>
      </form>
      <div className="placeholder"></div>
    </div>
  );
};

export default PatientForm;
